'use client';

import { memo, useState } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';

import { Question } from '@entities/quiz';
import { Checkbox } from '@shared/ui/components';

import styles from './MultipleQuestion.module.scss';

interface MultipleQuestionProps {
  index: number;
  question: Question;
  rightAnswerIds: string[];
  className?: string;
}

const MultipleQuestion = memo((props: MultipleQuestionProps) => {
  const { index, question, rightAnswerIds, className } = props;

  const t = useTranslations('quiz');

  const [values, setValues] = useState<string[]>([]);

  const toggleValue = (value: string) => {
    const newValues = values.filter((v) => v !== value);

    if (newValues.length < values.length) {
      setValues(newValues);
    } else {
      setValues([...newValues, value]);
    }
  };

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.scope}>
        <div className={styles.question_title}>
          {t('question')} #{index}
        </div>

        <div className={styles.question_text}>{question.text}</div>
      </div>

      <div className={styles.scope}>
        <div className={styles.answer_title}>{t('yourAnswer')}</div>

        <div className={styles.answer_content}>
          {question.answers.map((answer) => (
            <Checkbox
              key={answer.id}
              checked={values.includes(answer.id)}
              label={
                <span
                  className={clsx(
                    rightAnswerIds.length &&
                      rightAnswerIds.includes(answer.id) &&
                      styles.label_correct,
                    rightAnswerIds.length &&
                      values.includes(answer.id) &&
                      !rightAnswerIds.includes(answer.id) &&
                      styles.label_wrong,
                  )}
                >
                  {answer.text}
                </span>
              }
              disabled={rightAnswerIds.length > 0}
              className={styles.checkbox}
              onChange={() => toggleValue(answer.id)}
            />
          ))}
        </div>

        {rightAnswerIds.length > 0 && (
          <div
            className={clsx(
              styles.result,
              rightAnswerIds.length === values.length &&
                rightAnswerIds.every((v) => values.includes(v))
                ? styles.result_correct
                : styles.result_wrong,
            )}
          >
            {t(
              rightAnswerIds.length === values.length &&
                rightAnswerIds.every((v) => values.includes(v))
                ? 'correctAnswer'
                : 'wrongAnswer',
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default MultipleQuestion;
